<script setup>
import { computed } from 'vue'
import { usePremiumStore } from '@stores/premium'
import { useAnalytics } from '@composables/use-analytics'
import { FOOTER_COLUMNS, FOOTER_SOCIAL, PREMIUM_COLUMN_ID } from '@data/footer-links'

const premiumStore = usePremiumStore()
const analyticsService = useAnalytics()

// Logged-out visitors are free listeners too (PRD rule 7) — the sidebar's Go Premium rule.
// Subscribers lose the column outright; the grid's fixed tracks keep the rest in their cells.
const columns = computed(() =>
    premiumStore.isPremium ? FOOTER_COLUMNS.filter(column => column.id !== PREMIUM_COLUMN_ID) : FOOTER_COLUMNS
)

function onInAppLinkClick(column) {
    if (column.id === PREMIUM_COLUMN_ID) analyticsService.premiumUpsellInitiated('footer')
}

// Year-granular, so a server/client render can only disagree across a New Year boundary.
const year = new Date().getFullYear()
</script>

<template>
    <footer class="app-footer">
        <div class="footer-inner">
            <div class="footer-body">
                <div class="footer-columns">
                    <div
                        v-for="column in columns"
                        :key="column.id"
                        class="footer-column"
                    >
                        <h2 class="footer-heading">{{ column.heading }}</h2>
                        <ul class="footer-list">
                            <li
                                v-for="link in column.links"
                                :key="link.label"
                            >
                                <router-link
                                    v-if="link.to"
                                    class="footer-link"
                                    :to="link.to"
                                    @click="onInAppLinkClick(column)"
                                >{{ link.label }}</router-link>
                                <a
                                    v-else
                                    class="footer-link"
                                    :href="link.href"
                                    target="_blank"
                                    rel="noopener"
                                >{{ link.label }}</a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="footer-social">
                    <a
                        v-for="social in FOOTER_SOCIAL"
                        :key="social.id"
                        :class="['footer-social-link', `footer-social-${social.id}`]"
                        :href="social.href"
                        :aria-label="social.label"
                        target="_blank"
                        rel="noopener"
                    >
                        <!-- No handler: a plain <a target=_blank> must own the click (a nested button is the activation target and some engines won't follow the link). The link supplies the hover/press states below, and the only tab stop. -->
                        <BaseButton
                            variant="secondary"
                            size="m"
                            icon-only
                            :icon="social.icon"
                            :label="social.label"
                            tabindex="-1"
                        />
                    </a>
                </div>
            </div>
            <div class="footer-copyright">© {{ year }} FRISKY</div>
        </div>
    </footer>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// Figma Footer 511:229: hairline · links band (5 headed columns + social discs) · hairline · copyright.
.app-footer {
    margin-top: $space-64;
    border-top: 1px solid $c-border-default;

    @include sidePadding;
}

.footer-inner {
    @include container;
}

// Desktop: columns and discs share one row, discs right. Below desktop the discs drop to
// their own row under the columns.
.footer-body {
    display: flex;
    gap: $space-32;
    align-items: flex-start;
    justify-content: space-between;
    padding: $space-48 0;
    border-bottom: 1px solid $c-border-default;

    @include tabletOnly {
        flex-direction: column;
    }

    @include mobileOnly {
        flex-direction: column;
        padding: $space-40 0;
    }
}

// A FIXED track count per tier (never auto-fit): without the Premium column (subscribers)
// the others flow into the same cells and the last one stays empty — Figma's
// "grid cell (empty)" spacers. No re-spacing.
.footer-columns {
    display: grid;
    flex: 1 1 0;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: $space-32 $space-24;
    min-width: 0;

    @include tabletOnly {
        flex: none;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
    }

    @include mobileOnly {
        flex: none;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: $space-16;
        width: 100%;
    }
}

.footer-heading {
    @include caption;

    margin: 0 0 $space-12;
    color: $c-text-secondary;
}

// Figma: 12 between heading and links, 30 link pitch. Each link keeps 4px of hit padding
// top and bottom, so the list pulls itself back by 4 at both ends and gaps the rest by 4.
.footer-list {
    display: flex;
    flex-direction: column;
    gap: $space-4;
    padding: 0;
    margin: (-$space-4) 0;
    list-style: none;
}

.footer-link {
    @include secondary;

    display: inline-block;
    padding: $space-4 0;
    color: $c-text-tertiary;
    text-decoration: none;

    &:visited {
        color: $c-text-tertiary;
    }

    &:hover {
        color: $c-text-primary;
        text-decoration: none;
    }
}

.footer-social {
    display: flex;
    flex-shrink: 0;
    gap: $space-12;
    align-items: flex-start;
}

// The link, not the pill inside it, takes the click: the handler-less pill is
// `cannot-be-hovered` (pointer-events: none), so the link is sized to the pill (#354 ruling)
// and supplies the states the pill can't have, mirroring BaseButton's secondary recipe
// (state/hover and state/pressed over surface/raised) — the bfhs-button.vue precedent.
.footer-social-link {
    display: inline-flex;
    border-radius: $radius-full;

    &:hover .base-button-secondary {
        background: linear-gradient($c-state-hover, $c-state-hover) $c-surface-raised;
    }

    // Press mirrors BaseButton's too: pressed overlay + the 0.97 scale, no scale under
    // reduced motion.
    &:active .base-button-secondary {
        background: linear-gradient($c-state-pressed, $c-state-pressed) $c-surface-raised;
        transform: scale(0.97);

        @media (prefers-reduced-motion: reduce) {
            transform: none;
        }
    }
}

// Figma marks: 18 in the 40 disc, Threads 20 — its artwork (logo/threads 53:14) carries
// built-in padding. BaseIcon emits width/height attributes, so set both axes.
.footer-social-link :deep(.base-button-icon) {
    @include square(18px);
}

.footer-social-link.footer-social-threads :deep(.base-button-icon) {
    @include square(20px);
}

.footer-copyright {
    @include secondary;

    padding: $space-24 0 $space-32;
    color: $c-text-tertiary;
}
</style>
